import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { SIMPLE_SIGNUP_SUBSCRIPTION, VERIFY_OTP_AND_LOGIN } from 'api/graphql/auth';
import { normalizePhone } from 'lib/validators';
import { useAuth } from '../context/AuthContext';

export const LOGIN_STEPS = { PHONE: 'phone', OTP: 'otp' };

/** Give up waiting for the OTP-dispatch subscription after this long. */
const OTP_REQUEST_TIMEOUT_MS = 30000;

/**
 * Drives the two-step phone + OTP login.
 *
 * OTP requests go through the `simpleSignupSubscription` (NOT the `simpleSignup`
 * mutation): SMS delivery is async (Kafka-backed), and the subscription emits
 * `success: true` once the code is actually dispatched — mirroring the
 * production app. We then move to OTP entry; `verifyOtpAndLogin` finishes login.
 */
export function usePhoneLogin() {
  const client = useApolloClient();
  const { login } = useAuth();

  const [step, setStep] = useState(LOGIN_STEPS.PHONE);
  const [phone, setPhone] = useState('');
  const [devOtp, setDevOtp] = useState(null);
  const [error, setError] = useState(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  const subscriptionRef = useRef(null);
  const timeoutRef = useRef(null);

  const [runVerify, verifyState] = useMutation(VERIFY_OTP_AND_LOGIN);

  const teardown = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Tear down any in-flight subscription if the component unmounts.
  useEffect(() => teardown, [teardown]);

  const requestOtp = useCallback(
    (rawPhone) =>
      new Promise((resolve) => {
        setError(null);
        const normalized = normalizePhone(rawPhone);
        setIsSendingOtp(true);
        teardown();

        const settle = (ok) => {
          setIsSendingOtp(false);
          teardown();
          resolve(ok);
        };

        const observable = client.subscribe({
          query: SIMPLE_SIGNUP_SUBSCRIPTION,
          variables: { phone: normalized },
        });

        subscriptionRef.current = observable.subscribe({
          next({ data }) {
            const response = data?.simpleSignupSubscription;
            if (response?.success) {
              setPhone(normalized);
              setDevOtp(response.otp || null); // echoed only when not delivered (non-prod)
              setStep(LOGIN_STEPS.OTP);
              settle(true);
            } else if (response?.error) {
              setError(response.error.message || 'Failed to send the OTP.');
              settle(false);
            }
            // Otherwise it's an intermediate status update — keep waiting.
          },
          error(err) {
            setError(err?.message || 'Failed to send the OTP. Please try again.');
            settle(false);
          },
        });

        timeoutRef.current = setTimeout(() => {
          setError('Timed out while sending the OTP. Please try again.');
          settle(false);
        }, OTP_REQUEST_TIMEOUT_MS);
      }),
    [client, teardown],
  );

  const verifyOtp = useCallback(
    async (rawOtp) => {
      setError(null);
      try {
        const { data } = await runVerify({
          // Send the OTP as issued (trim whitespace only).
          variables: { phone, otp: String(rawOtp).trim() },
        });
        const result = data?.verifyOtpAndLogin;
        if (!result?.success || !result?.token?.authToken) {
          setError(result?.message || 'That OTP did not match. Please try again.');
          return false;
        }
        login({
          token: result.token.authToken,
          refreshToken: result.token.refreshToken,
          user: result.user,
        });
        return true;
      } catch (err) {
        setError(err.message);
        return false;
      }
    },
    [phone, runVerify, login],
  );

  const reset = useCallback(() => {
    teardown();
    setStep(LOGIN_STEPS.PHONE);
    setDevOtp(null);
    setError(null);
    setIsSendingOtp(false);
  }, [teardown]);

  return {
    step,
    phone,
    devOtp,
    error,
    isSendingOtp,
    isVerifying: verifyState.loading,
    requestOtp,
    verifyOtp,
    resendOtp: () => requestOtp(phone),
    reset,
  };
}
